/**
 * 用户数据缓存工具
 * 用于在 localStorage 中保存和读取用户信息，提升页面刷新时的用户体验
 */

const CACHE_KEY = 'ls_user_cache';
const CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7天缓存过期时间

export interface User {
  id: number;
  name: string;
  surname?: string;
  phoneNumber?: string;
  avatar?: string;
  cpf?: string;
  email?: string;
  username?: string;
  nickName?: string;
  profileName?: string;
  birthday?: string;
  followerCount?: number;
  followingCount?: number;
  postCount?: number;
  hideBadges?: boolean;
  hideStatistics?: boolean;
  isGuest?: boolean; // 标识是否为临时用户（未登录）
}

export interface CachedUserData {
  user: User | null;
  userId: number | null;
  cartCount: number;
  notificationsCount: number;
  timestamp: number;
}

/**
 * 保存用户数据到 localStorage
 */
export const saveUserCache = (data: {
  user: User | null;
  userId: number | null;
  cartCount: number;
  notificationsCount: number;
}): void => {
  try {
    const cacheData: CachedUserData = {
      ...data,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('保存用户缓存失败:', error);
  }
};

/**
 * 从 localStorage 读取用户数据
 * 如果缓存过期或无效，返回 null
 */
export const loadUserCache = (): CachedUserData | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) {
      return null;
    }

    const cacheData: CachedUserData = JSON.parse(cached);
    
    // 检查缓存是否过期
    if (Date.now() - cacheData.timestamp > CACHE_EXPIRY_MS) {
      clearUserCache();
      return null;
    }

    // 验证缓存数据结构
    if (!cacheData.user || !cacheData.userId) {
      return null;
    }

    return cacheData;
  } catch (error) {
    console.warn('读取用户缓存失败:', error);
    return null;
  }
};

/**
 * 清除 localStorage 中的用户缓存
 */
export const clearUserCache = (): void => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    console.warn('清除用户缓存失败:', error);
  }
};

/**
 * 检查是否有有效的用户缓存
 */
export const hasValidUserCache = (): boolean => {
  return loadUserCache() !== null;
};



